const { identity, scale: tmScale, rotate, translate, compose } = window.TransformationMatrix;

function matrixIsIdentity(m, epsilon = 1e-6)
{
    const id = identity();
    for(let key of Object.keys(m))
    {
        if(Math.abs(m[key] - id[key]) > epsilon)
            return false;
    }
    return true;
}

export function waitForImageLoad(img, { signal } = {})
{
    return new Promise((resolve, reject) => {
        let cleanupAbort = new AbortController();
        let cleanupSignal = cleanupAbort.signal;
        if(signal?.aborted)
        {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }

        if(signal)
            signal.addEventListener("abort", () => cleanupAbort.abort(), { once: true });

        if(img.complete && img.naturalWidth !== 0)
        {
            resolve();
            return;
        }

        img.addEventListener("load", () => {
            cleanupAbort.abort();
            resolve();
        }, { signal: cleanupSignal });

        img.addEventListener("error", () => {
            cleanupAbort.abort();
            reject(new Error(`Failed to load image: ${img.src}`));
        }, { signal: cleanupSignal });
    });
}

export function asyncRAF(signal)
{
    return new Promise((resolve, reject) => {
        let cancel = () => {
            cancelAnimationFrame(handle);
            resolve(null);
        };
        let handle = requestAnimationFrame((time) => {
            resolve(time);
            signal.removeEventListener("abort", cancel);
        });

        signal.addEventListener("abort", cancel, { once: true });
    });
}

// A one-shot dragger for a singe interaction.  This is used for dragging
// corner handles.
export class PointDragger
{
    constructor(node, {
        onmove,
        onend,
        canvas,
        centerPos,
        pointerEvent,
        signal: parentSignal,
    })
    {
        this._abort = new AbortController();
        let signal = this._signal = this._abort.signal;
        if(parentSignal)
            parentSignal.addEventListener("abort", () => this._abort.abort());

        this.node = node;
        this.canvas = canvas;
        this._callbacks = {
            onmove,
            onend,
        };

        this.onpointermove = this.onpointermove.bind(this);
        this._pointerId = pointerEvent.pointerId;
        console.log("PointDragger pointerId:", this._pointerId);

        window.addEventListener("pointerup", (e) => this.onpointerup(e), { signal });
        window.addEventListener("pointercancel", (e) => this.onpointerup(e), { signal });
        window.addEventListener("pointermove", (e) => this.onpointermove(e), { signal, passive: false });

        // Prevent long-press on iOS:
        node.addEventListener("touchstart", (e) => e.preventDefault(), { signal, passive: false });

        // Pretend that this is a two-touch gesture, with one touch at the center of the canvas
        // and one at the pointer position.
        let rect = this.canvas.getBoundingClientRect();
        this._centerPos = centerPos;

        // Create the TouchTransform and set the initial position.
        this._touchTransform = new TouchTransform();
        let position = { x: pointerEvent.clientX, y: pointerEvent.clientY };
        this._touchTransform.reset(
            [this._centerPos, position],
            rect,
            { width: this.canvas.width, height: this.canvas.height });
    }

    onpointermove(event)
    {
        if(event.pointerId != this._pointerId)
            return;
        
        let { clientX, clientY } = event;
        let position = { x: clientX, y: clientY };
        let matrix = this._touchTransform.update([this._centerPos, position]);
        if(!matrixIsIdentity(matrix))
            this._callbacks.onmove?.({ matrix });
    }

    onpointerup(event)
    {
        if(event.pointerId != this._pointerId)
            return;

        this._callbacks.onend?.();
        this._abort.abort();
    }
}

// This handles touch drag, pinch and rotation, as well as click-drags on desktop.
export class TouchDragger
{
    constructor(node, {
        onstart,
        onmove,
        onend,
        canvas,
        signal
    })
    {
        this.node = node;
        this.canvas = canvas;
        this.signal = signal;
        this._touches = {};
        this._callbacks = {
            onstart,
            onmove,
            onend,
        };

        this.onpointermove = this.onpointermove.bind(this);

        node.addEventListener("pointerdown", (e) => this.onpointerdown(e), { signal });
        window.addEventListener("pointerup", (e) => this.onpointerup(e), { signal });
        window.addEventListener("pointercancel", (e) => this.onpointerup(e), { signal });

        // Prevent long-press on iOS:
        node.addEventListener("touchstart", (e) => e.preventDefault(), { signal, passive: false });
    }

    onpointerdown(event)
    {
        if(this._touches[event.pointerId])
        {
            console.log(`Already tracking pointer ${event.pointerId}`);
            return;
        }

        let isFirstTouch = Object.keys(this._touches).length == 0;

        let { clientX, clientY } = event;
        this._touches[event.pointerId] = { clientX, clientY };

        if(isFirstTouch)
            this.startUpdates({ initialEvent: event, clientX, clientY });
    }

    onpointermove(event)
    {
        let touchInfo = this._touches[event.pointerId];
        if(!touchInfo)
        {
            console.log(`Not tracking pointer ${event.pointerId}`);
            return;
        }
        
        let { clientX, clientY } = event;
        touchInfo.clientX = clientX;
        touchInfo.clientY = clientY;
    }

    onpointerup(event)
    {
        if(!this._touches[event.pointerId])
        {
            console.log(`Not tracking pointer ${event.pointerId}`);
            return;
        }

        delete this._touches[event.pointerId];
        if(Object.keys(this._touches).length == 0)
            this.stopUpdates();
    }

    // Start handling a drag.
    async startUpdates({ initialEvent, clientX, clientY })
    {
        if(this._abortUpdateLoop)
        {
            console.log("Update loop already running");
            return;
        }

        window.addEventListener("pointermove", this.onpointermove, { signal: this.signal, passive: false });

        let lastPositions = this._touchPositions;
        this._callbacks.onstart?.({ initialEvent, clientX, clientY });

        // Create the TouchTransform and set the initial position.
        this._touchTransform = new TouchTransform();
        this.resetTouchTransform(lastPositions);

        this._abortUpdateLoop = new AbortController();
        let signal = this._abortUpdateLoop.signal;
        while(!signal.aborted)
        {
            await asyncRAF(this.signal);
            if(signal.aborted)
                return;

            let newPositions = this._touchPositions;
            if(newPositions.count != lastPositions.count)
            {
                // Reset the transform when the number of touches changes.
                this.resetTouchTransform(newPositions);
            }
            else
            {
                let matrix = this._touchTransform.update(newPositions.touches);
                if(!matrixIsIdentity(matrix))
                    this._callbacks.onmove?.({ matrix });
            }

            lastPositions = newPositions;
        }
    }

    resetTouchTransform(touchPositions)
    {
        let rect = this.canvas.getBoundingClientRect();
        this._touchTransform.reset(
            touchPositions.touches,
            rect,
            { width: this.canvas.width, height: this.canvas.height });
    }

    stopUpdates()
    {
        if(!this._abortUpdateLoop)
            return;

        window.removeEventListener("pointermove", this.onpointermove);

        // Shut down the update loop.
        this._abortUpdateLoop.abort();
        this._abortUpdateLoop = null;

        this._callbacks.onend?.();
    }

    get _touchPositions()
    {
        let count = 0;
        let touches = [];
        for(let pointerId in this._touches)
        {
            let touch = this._touches[pointerId];
            touches.push({ x: touch.clientX, y: touch.clientY });
            count++;
        }

        return { touches, count };
    }
}

export class TouchTransform {
    constructor() {
        this._accum = identity();
        this._prevTouches = [];
        this._rect = null;
        this._canvasW = 1;
        this._canvasH = 1;
        this._sx = 1; // client->canvas scale X
        this._sy = 1; // client->canvas scale Y
    }

    // See prompt for parameter semantics
    reset(inputList, boundingRect, { width, height }) {
        if (!Array.isArray(inputList) || inputList.length === 0) {
            throw new Error("reset requires at least one touch point");
        }
        this._prevTouches = inputList.map(p => ({ x: p.x, y: p.y }));
        this._rect = boundingRect;
        this._canvasW = width;
        this._canvasH = height;

        const rw = Math.max(1e-6, boundingRect.width);
        const rh = Math.max(1e-6, boundingRect.height);
        this._sx = width / rw;
        this._sy = height / rh;
        // Note: we do NOT change this._accum here by design.
    }

    // Update with same number of touches as the last reset()
    update(inputList) {
        if (!this._rect) {
            throw new Error("reset() must be called before update()");
        }
        if (!Array.isArray(inputList) || inputList.length !== this._prevTouches.length) {
            throw new Error("update() must be called with the same number of touches used in reset()");
        }

        const prev = this._prevTouches;
        const curr = inputList;

        // Centroids in client space
        const c0 = centroid(prev);
        const c1 = centroid(curr);

        // Derive scale (uniform) from average radius to centroid
        const { avgR0, avgR1 } = averageRadii(prev, curr, c0, c1);
        let s = 1;
        if (avgR0 > 1e-6 && curr.length >= 2) {
            s = clamp(avgR1 / avgR0, 1e-4, 1e4);
        }

        // Derive rotation (radians) from average delta of angles, weighted by prev radius
        let theta = 0;
        if (curr.length >= 2) {
            theta = averageAngleDelta(prev, curr, c0, c1);
        }

        // Map the gesture centers into canvas space
        const C0 = this._clientToCanvas(c0);
        const C1 = this._clientToCanvas(c1);

        let cDelta = { x: C1.x - C0.x, y: C1.y - C0.y };
        const delta =
            compose(
                // Translation delta:
                translate(cDelta.x, cDelta.y),

                // Shift the current position to the origin:
                translate(C1.x, C1.y),
                rotate(theta),
                tmScale(s, s),                 // uniform scale
                translate(-C1.x, -C1.y),
            );

        // Accumulate and return the full transform
        this._accum = compose(delta, this._accum);

        // Set new baseline for the next frame
        this._prevTouches = curr.map(p => ({ x: p.x, y: p.y }));

        return this._accum;
    }

    get matrix() {
        return this._accum;
    }

    // Helpers
    _clientToCanvas(pt) {
        // Convert client coordinates inside the bounding rect into canvas space
        let x = (pt.x - this._rect.left) * this._sx;
        let y = (pt.y - this._rect.top) * this._sy;
        return { x, y };
    }
}

function centroid(points) {
    let sx = 0, sy = 0;
    for(let i = 0; i < points.length; i++)
    {
        sx += points[i].x;
        sy += points[i].y;
    }

    let inv = 1 / points.length;
    return { x: sx * inv, y: sy * inv };
}

function averageRadii(prev, curr, c0, c1) {
    let sum0 = 0, sum1 = 0;
    for(let i = 0; i < prev.length; i++)
    {
        let v0x = prev[i].x - c0.x, v0y = prev[i].y - c0.y;
        let v1x = curr[i].x - c1.x, v1y = curr[i].y - c1.y;
        sum0 += Math.hypot(v0x, v0y);
        sum1 += Math.hypot(v1x, v1y);
    }

    let inv = 1 / prev.length;
    return { avgR0: sum0 * inv, avgR1: sum1 * inv };
}

function averageAngleDelta(prev, curr, c0, c1)
{
    // Weighted by previous radius to reduce noise near the center
    let sumSin = 0, sumCos = 0, wsum = 0;
    for(let i = 0; i < prev.length; i++)
    {
        const v0x = prev[i].x - c0.x, v0y = prev[i].y - c0.y;
        const v1x = curr[i].x - c1.x, v1y = curr[i].y - c1.y;

        const a0 = Math.atan2(v0y, v0x);
        const a1 = Math.atan2(v1y, v1x);
        const w = Math.hypot(v0x, v0y);

        // Robust mean on the circle: sum unit vectors of angle differences
        const d = normalizeAngle(a1 - a0);
        sumCos += Math.cos(d) * w;
        sumSin += Math.sin(d) * w;
        wsum += w;
    }

    if(wsum < 1e-6)
        return 0;
    return Math.atan2(sumSin, sumCos); // weighted circular mean
}

function normalizeAngle(a)
{
    // Map to (-π, π]
    while(a <= -Math.PI)
        a += 2 * Math.PI;
    while(a > Math.PI)
        a -= 2 * Math.PI;
    return a;
}

function clamp(v, lo, hi)
{
    return Math.max(lo, Math.min(hi, v));
}

export function canvasToDom(canvas, { x, y })
{
    // Convert from the canvas's coordinates to CSS pixels.
    let rect = canvas.getBoundingClientRect();
    let scaleX = rect.width  / canvas.width;
    let scaleY = rect.height / canvas.height;

    let clientLeft = rect.left + x * scaleX;
    let clientTop  = rect.top  + y * scaleY;

    return {
        x: clientLeft - rect.left,
        y: clientTop  - rect.top,
        clientLeft, clientTop,
    };
}
